import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Form, Input, message, Radio, Row, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import AuthCardComponent from "../../../components/login-card/auth-card.component";
import LilyButton from "../../../components/button/lily-button";
import { EDIT_REIMBURSEMENT_URL } from "../../../api/URLs";
import portalAPI from "../../../api/api";
import {
  detectChanges,
  flattenObject,
  getUpdatedFields,
} from "../../../exports/util";
import { updateUser } from "../../../stores/authenticated-user.store";
import { StatesOption, formatStateCode } from "../../../exports/states-list";
import { useWatch } from "antd/es/form/Form";
import BackButton from "../../../components/button/back-button";

const EditCheckPage = () => {
  const user = useSelector((state) => state.authenticatedUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  let initValue = {
    option: "",
    recipientName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
  };

  const [isLoading, setIsLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [entityRecord, setEntityRecord] = useState(initValue);
  const [dataChanges, setDataChanges] = useState(initValue);
  const selectedOption = useWatch("option", form);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDataChanges((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const populateFields = () => {
    const useUserAddress = selectedOption === "yes";

    const updatedFields = {
      recipientName: useUserAddress ? user?.fullName || "" : "",
      address1: useUserAddress ? user?.address?.line1 || "" : "",
      address2: useUserAddress ? user?.address?.line2 || "" : "",
      city: useUserAddress ? user?.address?.city || "" : "",
      state: useUserAddress ? formatStateCode(user?.address?.state) : "",
      zip: useUserAddress ? user?.address?.zip || "" : "",
    };

    form.setFieldsValue(updatedFields);
  };

  const getTransformedFormValues = (formValues) => ({
    _id: originalData?._id || "",
    method: "check-by-mail",
    option: formValues.option || "",
    checkByMail: {
      recipientName: formValues.recipientName || "",
      address1: formValues.address1 || "",
      address2: formValues.address2 || "",
      city: formValues.city || "",
      state: formValues.state || "",
      zip: formValues.zip || "",
    },
  });

  useEffect(() => {
    if (entityRecord && entityRecord.option === selectedOption) {
      form.setFieldsValue(entityRecord);
    } else {
      if (selectedOption === "yes" || selectedOption === "no") {
        populateFields();
      }
    }
    form.setFields([
      { name: "recipientName", errors: [] },
      { name: "address1", errors: [] },
      { name: "address2", errors: [] },
      { name: "city", errors: [] },
      { name: "state", errors: [] },
      { name: "zip", errors: [] },
    ]);
  }, [selectedOption, form, entityRecord]);

  useEffect(() => {
    const methodData = user.reimbursementMethods.find(
      (m) => m.method === "check-by-mail",
    );
    let record = {
      option: methodData?.option || "",
      recipientName: methodData?.checkByMail?.recipientName || "",
      address1: methodData?.checkByMail?.address1 || "",
      address2: methodData?.checkByMail?.address2 || "",
      city: methodData?.checkByMail?.city || "",
      state: formatStateCode(methodData?.checkByMail?.state),
      zip: methodData?.checkByMail?.zip || "",
    };
    setOriginalData(methodData);
    setEntityRecord(record);
    setDataChanges(record);
    form.setFieldsValue(record);
  }, [user]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const values = await form.validateFields();

      const reimbursement_id = originalData?._id;
      if (!reimbursement_id) {
        message.error("Check ID not found");
        return;
      }

      const updatedData = getTransformedFormValues(values);
      const changes = getUpdatedFields(originalData, updatedData);

      if (!changes || Object.keys(changes).length === 0) {
        message.info("No changes detected to update.");
        return;
      }

      const flattened = flattenObject(changes);

      const response = await portalAPI.put(EDIT_REIMBURSEMENT_URL, {
        reimbursement_id,
        updates: flattened,
      });

      if (response.data.status === 200) {
        dispatch(
          updateUser({
            key: "reimbursementMethods",
            value: response.data.data.reimbursementMethods,
          }),
        );
        message.success("Updated Reimbursement Methods Check successfully");
        navigate("/setting/reimbursement-methods");
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const defaultCheck = useMemo(() => {
    if (selectedOption === "yes") {
      return true;
    }
    return false;
  }, [selectedOption]);

  const defaultChanges = useMemo(() => {
    return detectChanges(dataChanges, entityRecord);
  }, [dataChanges, entityRecord]);

  return (
    <div className="user-setting-layout">
      <div className="w-100">
        <BackButton path="/setting/reimbursement-methods" />
      </div>

      <div className="w-100 d-flex justify-content-center mt-4">
        <AuthCardComponent
          title="Check Information"
          description="Prefer a paper check? Add your current mailing address to receive physical checks."
          buttonEle={
            <div className="w-100 d-flex justify-content-end mt-3">
              <LilyButton
                disabled={!defaultChanges}
                loading={isLoading}
                onClick={handleSubmit}
                text="Save Information"
              />
            </div>
          }
        >
          <Form layout="vertical" form={form}>
            <Form.Item
              name="option"
              label="Would you like to use the address on file?"
              rules={[{ required: true, message: "Please select an option." }]}
            >
              <Radio.Group
                className="d-flex flex-row gap-2"
                name="option"
                onChange={handleChange}
              >
                <Radio value="yes">Yes</Radio>
                <Radio value="no">No</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label="Recipient Name (as it should appear on check)"
              name="recipientName"
              rules={[
                { required: true, message: "Please enter recipient name." },
                { whitespace: true, message: "Name cannot be empty." },
              ]}
            >
              <Input
                disabled={isLoading || defaultCheck}
                name="recipientName"
                onChange={handleChange}
              />
            </Form.Item>

            <Form.Item
              label="Mailing Address"
              name="address1"
              rules={[
                { required: true, message: "Please enter mailing address." },
              ]}
            >
              <Input
                disabled={isLoading || defaultCheck}
                name="address1"
                onChange={handleChange}
              />
            </Form.Item>

            <Form.Item label="Mailing Address Line 2" name="address2">
              <Input
                disabled={isLoading || defaultCheck}
                name="address2"
                onChange={handleChange}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true, message: "Please enter city." }]}
                >
                  <Input
                    disabled={defaultCheck}
                    name="city"
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label="State"
                  name="state"
                  rules={[
                    { required: true, message: "Please enter state." },
                    {
                      pattern: /^[A-Za-z\s]+$/,
                      message: "State must be alphabetic.",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    options={StatesOption()}
                    disabled={defaultCheck}
                    name="state"
                    onChange={(value) => {}}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label="ZIP"
                  name="zip"
                  rules={[
                    { required: true, message: "Please enter ZIP code." },
                    { pattern: /^[0-9]{5}$/, message: "ZIP must be 5 digits." },
                  ]}
                >
                  <Input
                    maxLength={10}
                    disabled={defaultCheck}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </AuthCardComponent>
      </div>
    </div>
  );
};

export default EditCheckPage;
